import axios from "axios";
import { getToken } from "../auth/msalConfig";
import UserFactory from "../factories/UserFactory";
import { stringifySearchParams } from "../utils";

const buildKilnBackendClient = async () => {
  const token = await getToken();
  return axios.create({
    baseURL: import.meta.env.VITE_KILN_BACKEND_BASEURL || "/",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    validateStatus: null,
  });
};

export const getCurrentUser = async () => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.get("/api/users/current");
};

export const getUsers = async () => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.get("/api/users");
};

export const getPoeUsers = async () => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.get("/api/users/poeUserList");
};

export const getDepartedContractorUsersClass = async () => {
  const kilnBackendClient = await buildKilnBackendClient();
  const response = await kilnBackendClient.get("/api/users/departed/contractor");
  return UserFactory.createFromArray(response.data || []);
};

export const getManagedUsers = async () => {
  const kilnBackendClient = await buildKilnBackendClient();
  const resp = await kilnBackendClient.get("/api/users/managed");
  return UserFactory.createFromArray(resp.data || []);
};

export const getUsersClass = async () => {
  const response = await getUsers();
  return UserFactory.createFromArray(response.data || []);
};

export const checkPermission = async (type) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.get(`/api/forms/${type}/authority`);
};

export const checkPoePermission = async () => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.get(`/api/proof-of-employment-forms/poe/authority`);
};

export const getAllPermanentEmployees = async () => {
  const kilnBackendClient = await buildKilnBackendClient();
  const response = await kilnBackendClient.get(`/api/users/permanent-employees`);
  return UserFactory.createFromArray(response.data);
};

// Includes everyone who directly or indirectly reports to the specified manager
export const getAllManagedPermanentEmployees = async () => {
  const kilnBackendClient = await buildKilnBackendClient();
  const response = await kilnBackendClient.get(`/api/users/managed-permanent-employees`);
  return UserFactory.createFromArray(response.data);
};

/************** FILES ENDPOINTS ********************/

export const uploadFile = async (file) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.post("/api/files", file, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const getDownloadLink = async (query) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return (await kilnBackendClient.get(`/api/files?${stringifySearchParams(query)}`)).data;
};

/************** DEPARTURE ENDPOINTS ********************/

export const submitDepartureForm = async (data) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.post("/api/departure-forms", data);
};

export const submitEditDepartureForm = async (id, data) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.patch(`/api/departure-forms/${id}`, data);
};

export const cancelDepartureForm = async (formId, cancelReason) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.patch(`/api/departure-forms/${formId}/cancel`, {
    cancelReason,
  });
};

export const getForms = async (query) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.get(`/api/departure-forms?${stringifySearchParams(query)}`);
};

export const getDepartureFormByIdResponse = async (formId) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.get(`/api/departure-forms/${formId}`);
};

/************** PARENTAL LEAVE ENDPOINTS ********************/

export const checkPermissionForParentalLeave = async () => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.get(`/api/parental-leave-forms/check/authority`);
};

export const submitParentalLeaveForm = async (data) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.post("/api/parental-leave-forms", data);
};

/************** PURCHASED LEAVE ENDPOINTS ********************/

export const checkPermissionForPurchasedLeave = async () => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.get("/api/purchased-leave-forms/check/authority");
};

export const checkPurchasedLeaveWindow = async () => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.get("/api/purchased-leave-forms/check/window");
};

export const getPurchasedLeaveEligibilityForEmployee = async (employeeId) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.get(`/api/purchased-leave-forms/eligibility/${employeeId}`);
};

export const getPurchasedLeaveForms = async (query) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.get(`/api/purchased-leave-forms?${stringifySearchParams(query)}`);
};

export const getPurchasedLeaveFormById = async (formId) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.get(`/api/purchased-leave-forms/${formId}`);
};

export const approvePurchasedLeaveForm = async (formId, data) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.patch(`/api/purchased-leave-forms/${formId}/approve`, data);
};

export const rejectPurchasedLeaveForm = async (formId, data) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.patch(`/api/purchased-leave-forms/${formId}/reject`, data);
};

export const submitPurchasedLeaveForm = async (data) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.post("/api/purchased-leave-forms", data);
};

/************** THIRD-PARTY CONTRACTOR ENDPOINTS ********************/
export const submitThirdPartyContractorForm = async (data) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.post("/api/third-party-contractor-forms", data);
};

export const getThirdPartyContractorForms = async (query) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.get(`/api/third-party-contractor-forms?${stringifySearchParams(query)}`);
};
export const getValidThirdPartyCompanies = async () => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.get(`/api/third-party-company/active-companies`);
};

export const getThirdPartyContractorExpiry = async (query) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.get(`/api/users/contractor-expiry?${stringifySearchParams(query)}`);
};
export const duplicateSearch = async (query) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.post(`/api/duplicate-search`, query);
};
/************** ORGANISATION ENDPOINTS ********************/

export const getFunctions = async () => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.get("/api/org/functions");
};

export const getProjectCostCentres = async () => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.get("/api/org/project-cost-centres");
};

export const getPositionTitles = async ({ excludeCOA } = {}) => {
  const kilnBackendClient = await buildKilnBackendClient();
  const params = excludeCOA ? { excludeCOA: true } : {};
  return await kilnBackendClient.get("/api/org/position-titles", { params });
};

export const getFunctionLobProducts = async () => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.get("/api/org/function-lob-products");
};

export const getRegionLocations = async () => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.get("/api/org/region-locations");
};

export const getProjects = async () => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.get("/api/org/projects");
};

export const getChannels = async () => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.get("/api/org/channels");
};

export const getCostCentres = async (functionFilter) => {
  const kilnBackendClient = await buildKilnBackendClient();
  const url = functionFilter
    ? `/api/org/cost-centres?function=${encodeURIComponent(functionFilter)}`
    : "/api/org/cost-centres";
  return await kilnBackendClient.get(url);
};

export const getAutoSelection = async (fieldType, parentSelections) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.post("/api/org/auto-selection", {
    fieldType,
    parentSelections,
  });
};

export const getRequiredFields = async (formType, packageConfig) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.post("/api/org/required-fields", {
    formType,
    packageConfig,
  });
};

export const validateRequiredFields = async (formData, formType, packageConfig) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.post("/api/org/validate-required-fields", {
    formData,
    formType,
    packageConfig,
  });
};

/************** ONBOARDING ENDPOINTS ********************/

export const getOnboardingFormByLeverOpportunityId = async (opportunityId) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.get(`/api/onboarding-forms/lever/${opportunityId}`);
};

export const getOnboardingFormById = async (id) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.get(`/api/onboarding-forms/${id}`);
};

export const submitOnboardingForm = async (data) => {
  const kilnBackendClient = await buildKilnBackendClient();
  const response = await kilnBackendClient.post("/api/onboarding-forms", data);

  return response;
};

export const updateOnboardingForm = async (formId, data) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.patch(`/api/onboarding-forms/${formId}`, data);
};

export const requestUpdateToOnboardingForm = async (formId, updateRequestReason) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.post(`/api/onboarding-forms/ticket/${formId}`, { data: updateRequestReason });
};

export const getOnboardingForms = async (query) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.get(`/api/onboarding-forms?${stringifySearchParams(query)}`);
};

export const cancelOnboardingForm = async (formId, cancelReason) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.patch(`/api/onboarding-forms/${formId}/cancel`, {
    cancelReason,
  });
};

export const reopenOnboardingForm = async (formId) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.patch(`/api/onboarding-forms/${formId}/reopen`);
};

export const resendOnboardingFlareInvite = async (id) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.patch(`/api/onboarding-forms/${id}/resend-flare-invite`);
};

export const skipOnboardingContract = async (formId, data) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.patch(`/api/onboarding-forms/${formId}/skip`, data);
};

export const skipOnboardingContractAndEmploymentForms = async (formId, data) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.patch(`/api/onboarding-forms/${formId}/skip-form`, data);
};

export const changeOnboardingStartDate = async (formId, startDate) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.patch(`/api/onboarding-forms/${formId}/change-start-date`, {
    data: startDate,
  });
};

export const checkPermissionForOnboarding = async (id) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.get(`/api/onboarding-forms/authority/${id}`);
};

/************** ROLE UPDATE ENDPOINTS ********************/

export const submitRoleUpdateForm = async (data) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.post("/api/role-update-forms", data);
};

export const checkPermissionForRoleUpdate = async (id) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.get(`/api/role-update-forms/authority/${id}`);
};

export const getRoleUpdateFormById = async (id) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.get(`/api/role-update-forms/${id}`);
};

export const updateRoleUpdateForm = async (formId, data) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.patch(`/api/role-update-forms/${formId}`, data);
};

export const cancelRoleUpdateForm = async (formId, cancelReason) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.patch(`/api/role-update-forms/${formId}/cancel`, {
    cancelReason,
  });
};

export const reopenRoleUpdateForm = async (id) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.patch(`/api/role-update-forms/${id}/reopen`);
};

export const resendRoleUpdateFlareInvite = async (id) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.patch(`/api/role-update-forms/${id}/resend-flare-invite`);
};

export const skipRoleUpdateContract = async (formId, data) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.patch(`/api/role-update-forms/${formId}/skip`, data);
};

export const skipRoleUpdateContractAndEmploymentForms = async (formId, data) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.patch(`/api/role-update-forms/${formId}/skip-form`, data);
};

export const getRoleUpdateForms = async (query) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.get(`/api/role-update-forms?${stringifySearchParams(query)}`);
};

export const getRoleUpdateFormByLeverOpportunityId = async (opportunityId) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.get(`/api/role-update-forms/lever/${opportunityId}`);
};

export const updateApprovalLevel = async (formId, levelApproval) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.patch(`/api/role-update-forms/${formId}/level-approval`, levelApproval);
};

/* ********** END-of-YEAR RATINGS ENDPOINTS ************** */

export const submitEoyRatings = async (data) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.post("/api/eoy-ratings/", data);
};

export const getRatingsCsvForYear = async (year) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.get(`/api/eoy-ratings/${year}`);
};

export const getManagerRatingsCsvForYear = async (year, type) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.get(`/api/eoy-ratings/manager/${type}/${year}`);
};

export const getEoyRatingsForm = async () => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.get("/api/eoy-ratings/");
};

export const checkIfFormIsOpen = async () => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.get("/api/eoy-ratings/check/form-open");
};

export const updateEoyRatingsForm = async (formId, data) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.patch(`/api/eoy-ratings/${formId}`, data);
};

/* ********** TALENT REVIEW ENDPOINTS ************** */

export const submitTalentReview = async (data) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.post("/api/talent-review/", data);
};

export const getManagerTalentReviewCsvForYear = async (year) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.get(`/api/talent-review/manager/${year}`);
};

export const checkManagerTalentReviewForYear = async (year) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.get(`/api/talent-review/manager/check/${year}`);
};

/************** PROOF OF EMPLOYMENT ENDPOINTS ********************/

export const getProofOfEmploymentFormById = async (id) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.get(`/api/proof-of-employment-forms/${id}`);
};

export const submitProofOfEmploymentForm = async (data) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.post("/api/proof-of-employment-forms", data);
};

export const updateProofOfEmploymentForm = async (formId, data) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.patch(`/api/proof-of-employment-forms/${formId}`, data);
};

export const checkPermissionForProofOfEmploymentUpdate = async (id) => {
  const kilnBackendClient = await buildKilnBackendClient();
  return await kilnBackendClient.get(`/api/proof-of-employment-forms/authority/${id}`);
};
